import React, { useMemo } from "react";
import { Container, Row, Col, Card, Form } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import HeaderDesktop from "../_components/_global/HeaderDesktop";

function useScopeId(prefix = "pfd") {
  return useMemo(() => `${prefix}-${Math.random().toString(36).slice(2, 8)}`, [prefix]);
}

export default function ProfileDesktop() {
  const scope = useScopeId();

  // —— demo 数据（可从后端/Context替换）——
  const user = {
    name: "Da pei",
    initials: "Pei",
    bio: "Designer who loves city travel and finds beauty in urban details.",
    location: "Toronto",
    joined: "Jan 2024",
  };
  const stats = [
    { label: "Cities Visited", value: 8, tone: "info" },
    { label: "Itineraries Completed", value: 12, tone: "success" },
    { label: "Reviews Written", value: 24, tone: "warn" },
    { label: "New Friends", value: 15, tone: "violet" },
  ];
  const badges = [
    { title: "City Explorer", desc: "Visit 10 different cities", cur: 8, total: 10, color: "#22c55e" },
    { title: "Review Master", desc: "Write 50 reviews", cur: 52, total: 50, color: "#22c55e" }, // 104%
    { title: "Social Star", desc: "Make 20 friends", cur: 15, total: 20, color: "#2563eb" },
    { title: "Navigation Pro", desc: "Use AI nav 100 times", cur: 78, total: 100, color: "#2563eb" },
  ];
  const trips = [
    { city: "Montreal", month: "Dec 2024", days: 5, rating: 5, cover: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR9BcrTUKhOfNSlkD4MtF6_xH2cmIVA8NiAOA&s" },
    { city: "Quebec City", month: "Nov 2024", days: 3, rating: 4, cover: "https://lp-cms-production.imgix.net/2024-11/500pxRF69947955.jpg?auto=format,compress&q=72&w=1440&h=810&fit=crop" },
    { city: "Guangzhou", month: "Oct 2024", days: 4, rating: 5, cover: "https://cdn.britannica.com/35/100235-050-CE3936EE/view-CN-Tower-Toronto-skyline-observation-deck.jpg" },
  ];

  const css = `
  [data-pfd="${scope}"] .banner{height:72px;border-radius:10px;background:linear-gradient(90deg,#5aaaff,#7b5bff);position:relative}
  [data-pfd="${scope}"] .edit{position:absolute;right:14px;top:14px;color:#fff;opacity:.9}

  [data-pfd="${scope}"] .card-soft{border:1px solid #e8edf2;border-radius:14px;background:#fff}
  [data-pfd="${scope}"] .user-row{display:flex;gap:14px;align-items:center;padding:18px}
  [data-pfd="${scope}"] .avatar{width:64px;height:64px;border-radius:50%;background:#f2f4f8;border:1px solid #e6eaf0;display:flex;align-items:center;justify-content:center;font-weight:800;color:#0f1720;font-size:24px}
  [data-pfd="${scope}"] .muted{color:#6a7a8a}

  [data-pfd="${scope}"] .stat{border-radius:12px;padding:24px;text-align:center;border:1px solid #eef2f6}
  [data-pfd="${scope}"] .stat.info{background:#f4f7ff}
  [data-pfd="${scope}"] .stat.success{background:#f2fbf6}
  [data-pfd="${scope}"] .stat.warn{background:#fff8e7}
  [data-pfd="${scope}"] .stat.violet{background:#f7f2ff}
  [data-pfd="${scope}"] .stat .num{font-size:28px;font-weight:800}
  [data-pfd="${scope}"] .stat .lab{color:#6a7a8a;margin-top:6px}

  [data-pfd="${scope}"] .section-title{display:flex;align-items:center;gap:8px;font-weight:800;color:#0f1720;margin:12px 0}
  [data-pfd="${scope}"] .badge-item{border:1px solid #e8edf2;border-radius:12px;background:#f5fdf7;padding:14px}
  [data-pfd="${scope}"] .prog{height:10px;background:#eaeef4;border-radius:999px;overflow:hidden}
  [data-pfd="${scope}"] .prog > span{display:block;height:100%;border-radius:999px}

  [data-pfd="${scope}"] .trip-row{border:1px solid #e8edf2;border-radius:12px;background:#f8fafc;padding:12px 14px;display:flex;align-items:center;gap:12px}
  [data-pfd="${scope}"] .thumb{width:56px;height:56px;border-radius:10px;overflow:hidden;background:#eef2f6;flex:0 0 56px}
  [data-pfd="${scope}"] .thumb img{width:100%;height:100%;object-fit:cover;display:block}
  [data-pfd="${scope}"] .stars{color:#f59e0b;font-size:16px}

  [data-pfd="${scope}"] .setting-row{display:flex;align-items:center;justify-content:space-between;padding:18px 14px;border-top:1px solid #eef2f6}
  [data-pfd="${scope}"] .setting-row:first-child{border-top:none}
  [data-pfd="${scope}"] .danger{color:#e11d48;font-weight:700}
  `;

  return (
    <>
      <style>{css}</style>

      {/* 共用桌面 Header，高亮 Profile */}
      <HeaderDesktop active="profile" cityName={user.location} unread={2} maxWidth={1280} />

      <Container data-pfd={scope} className="py-3">


        {/* 用户卡 */}
        <Card className="card-soft mb-3">
          <div className="user-row">
            <div className="avatar">{user.initials}</div>
            <div className="flex-grow-1">
              <div className="fw-bold" style={{ fontSize: 18 }}>{user.name}</div>
              <div className="muted">{user.bio}</div>
              <div className="muted mt-1">
                <i className="bi bi-geo-alt me-2" /> {user.location}
                <i className="bi bi-dot mx-1" />
                <i className="bi bi-calendar-week me-2" /> Joined {user.joined}
              </div>
            </div>
          </div>
        </Card>

        {/* 旅行统计 */}
        <Card className="card-soft mb-3 p-3">
          <div className="section-title">
            <i className="bi bi-trophy"></i> Travel Stats
          </div>
          <Row className="g-3">
            {stats.map((s) => (
              <Col md={6} key={s.label}>
                <div className={`stat ${s.tone}`}>
                  <div className="num">{s.value}</div>
                  <div className="lab">{s.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Card>

        {/* 成就徽章 */}
        <Card className="card-soft mb-3 p-3">
          <div className="section-title">
            <i className="bi bi-award"></i> Achievements
          </div>
          <Row className="g-3">
            {badges.map((b) => {
              const pct = Math.min(100, Math.round((b.cur / b.total) * 100));
              return (
                <Col md={12} key={b.title}>
                  <div className="badge-item">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="fw-bold">{b.title}</div>
                      <div className="muted">{pct}%</div>
                    </div>
                    <div className="muted">{b.desc}</div>
                    <div className="prog mt-2">
                      <span style={{ width: `${pct}%`, background: b.color }}></span>
                    </div>
                    <div className="muted mt-1">{b.cur}/{b.total}</div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Card>

        {/* 最近行程 */}
        <Card className="card-soft mb-3 p-3">
          <div className="section-title">
            <i className="bi bi-airplane"></i> Recent Trips
          </div>
          <div className="d-flex flex-column gap-2">
            {trips.map((t) => (
              <div className="trip-row" key={t.city}>
                <div className="thumb"><img src={t.cover} alt={t.city} loading="lazy" /></div>
                <div className="flex-grow-1">
                  <div className="fw-bold">{t.city}</div>
                  <div className="muted">{t.month} <span className="ms-2">{t.days} days</span></div>
                </div>
                <div className="stars">
                  {"★".repeat(t.rating)}{"☆".repeat(5 - t.rating)}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* 设置 */}
        <Card className="card-soft mb-4">
          <div className="section-title p-3 m-0">
            <i className="bi bi-gear"></i> Settings
          </div>
          <div className="setting-row">
            <div>
              <div className="fw-bold">Push Notifications</div>
              <div className="muted">Receive trip reminders and messages</div>
            </div>
            <Form.Check type="switch" defaultChecked />
          </div>
          <div className="setting-row">
            <div>
              <div className="fw-bold">Location Sharing</div>
              <div className="muted">Share real-time location with friends</div>
            </div>
            <Form.Check type="switch" defaultChecked />
          </div>
          <div className="setting-row">
            <div>
              <div className="fw-bold">Switch to Mobile Layout</div>
              <div className="muted">Current: Desktop</div>
            </div>
            <i className="bi bi-chevron-right"></i>
          </div>
          <div className="setting-row">
            <div className="fw-bold">Privacy</div>
            <i className="bi bi-chevron-right"></i>
          </div>
          <div className="setting-row">
            <div className="fw-bold">Help & Support</div>
            <i className="bi bi-chevron-right"></i>
          </div>
          <div className="setting-row">
            <div className="danger"><i className="bi bi-box-arrow-right me-1"></i> Log out</div>
          </div>
        </Card>
      </Container>
    </>
  );
}
